import { useCallback, useMemo } from "react";
import { asNumber } from "../../lib/kernelFormat.js";
import { sanitizeRenderColor } from "../../lib/renderSafety.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, colorForKey, fillStipplePath } from "./shared.jsx";

export const kind = "radar";

// Radar / spider chart. Each axis gets a per-axis max (declared on the
// axis spec, or inferred from the data column). Each row becomes one
// polygon; if seriesField is set, rows with the same series name get
// the same color (and rendered in render order).
export function Component({ spec, data, fillHeight = false }) {
    const model = useMemo(() => buildModel(spec, data), [spec, data]);
    const height = Math.max(160, Math.min(420, Number(spec.height) || 240));

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!model) return;
        const { axes, series } = model;
        const cx = width / 2;
        const cy = h / 2;
        const r = Math.min(width, h) / 2 - 24;
        const N = axes.length;
        if (N < 3) return;
        const line = resolveCssColor(root, "var(--line-soft)") || "#333";
        const muted = resolveCssColor(root, "var(--plot-ink-soft)") || "#888";

        // grid
        ctx.strokeStyle = line;
        ctx.lineWidth = 1;
        for (let g = 1; g <= 4; g++) {
            const rr = (r * g) / 4;
            ctx.beginPath();
            for (let i = 0; i < N; i++) {
                const a = -Math.PI / 2 + (i / N) * Math.PI * 2;
                const x = cx + Math.cos(a) * rr;
                const y = cy + Math.sin(a) * rr;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.stroke();
        }
        // spokes
        for (let i = 0; i < N; i++) {
            const a = -Math.PI / 2 + (i / N) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
            ctx.stroke();
        }

        // axis labels
        ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.fillStyle = muted;
        for (let i = 0; i < N; i++) {
            const a = -Math.PI / 2 + (i / N) * Math.PI * 2;
            const x = cx + Math.cos(a) * (r + 12);
            const y = cy + Math.sin(a) * (r + 12);
            const xText = Math.abs(Math.cos(a)) < 0.2
                ? "center"
                : (Math.cos(a) > 0 ? "left" : "right");
            ctx.textAlign = xText;
            ctx.textBaseline = "middle";
            ctx.fillText(truncate(String(axes[i].label), 12), x, y);
        }

        // polygons — stippled fill matches SurfaceChart aesthetic
        for (const s of series) {
            const c = resolveCssColor(root, s.color);
            const tracePoly = () => {
                ctx.beginPath();
                for (let i = 0; i < N; i++) {
                    const a = -Math.PI / 2 + (i / N) * Math.PI * 2;
                    const ratio = axes[i].max > 0
                        ? Math.max(0, Math.min(1, (s.values[i] ?? 0) / axes[i].max))
                        : 0;
                    const x = cx + Math.cos(a) * r * ratio;
                    const y = cy + Math.sin(a) * r * ratio;
                    if (i === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.closePath();
            };
            const bbox = { left: cx - r, top: cy - r, right: cx + r, bottom: cy + r };
            tracePoly();
            fillStipplePath(ctx, bbox, c, { density: 0.7 });
            tracePoly();
            ctx.strokeStyle = withAlpha(c, 0.85);
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }
    }, [model]);

    if (!model || !model.series.length) {
        return <KernelEmpty message={spec.empty || "no rows"} />;
    }

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            <CanvasSurface
                draw={draw}
                deps={[model]}
                height={fillHeight ? null : height}
                className={`w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]${fillHeight ? " flex-1 min-h-0" : ""}`}
            />
            {model.series.length > 1 ? (
                <div className="mt-[6px] flex flex-wrap gap-x-[12px] gap-y-[2px] text-[10px]">
                    {model.series.map((s, i) => (
                        <div key={i} className="flex items-center gap-[4px] text-[color:var(--muted)]">
                            <span
                                className="inline-block h-[8px] w-[8px] rounded-[1px]"
                                style={{ background: sanitizeRenderColor(s.color) }}
                            />
                            <span>{s.label}</span>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function buildModel(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    const axes = Array.isArray(spec.axes) ? spec.axes : [];
    if (!arr.length || axes.length < 3) return null;

    // per-axis max
    const axisModels = axes.map((ax) => {
        let mx = asNumber(ax.max);
        if (mx == null) {
            mx = 0;
            for (const row of arr) {
                const v = asNumber(row?.[ax.field]) ?? 0;
                if (v > mx) mx = v;
            }
            if (mx === 0) mx = 1;
        }
        return { label: ax.label || ax.field, field: ax.field, max: mx };
    });

    // group rows by series
    const seriesMap = new Map();
    for (const row of arr) {
        if (!row) continue;
        const key = spec.seriesField ? String(row[spec.seriesField] ?? "—") : "value";
        if (!seriesMap.has(key)) {
            seriesMap.set(key, []);
        }
        seriesMap.get(key).push(row);
    }
    // For each series, average values per axis. (Multiple rows of the
    // same series get summarised; single-row series just pass through.)
    const series = [...seriesMap.entries()].map(([key, rows]) => {
        const values = axisModels.map((ax) => {
            const sum = rows.reduce((s, r) => s + (asNumber(r[ax.field]) ?? 0), 0);
            return sum / rows.length;
        });
        const color = sanitizeRenderColor(
            spec.color && seriesMap.size === 1
                ? spec.color
                : colorForKey(key),
        );
        return { label: key, values, color };
    });
    return { axes: axisModels, series };
}

function truncate(s, n) {
    if (s.length > n) return s.slice(0, n - 1) + "…";
    return s;
}

function withAlpha(color, alpha) {
    if (!color) return color;
    if (color.startsWith("rgba(")) {
        return color.replace(/rgba\(([^)]+)\)/, (m, inner) => {
            const parts = inner.split(",").map((s) => s.trim());
            parts[3] = String(alpha);
            return `rgba(${parts.join(", ")})`;
        });
    }
    if (color.startsWith("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("#")) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return color;
}
